import { useMemo } from 'react';
import type { TaskEvent, TaskRef } from '@shared/types/tasks';
import { formatDateTime } from '@/lib/formatters';
import { FactorySuggestionsCard, type SuggestionPick } from './FactorySuggestionsCard';
import type { RoundUpstream } from './TaskToAnswer';
import { roundDecided, suggestionRoundId, suggestionRounds, suggestionsAnchorId, type SuggestionRound } from './task-suggestions';
import { formatRelative } from './task-ui';
import { useSuggestionTaskRefs } from './use-suggestion-task-refs';
import { useItemChats } from './use-suggestion-chats';
import { payloadConcerns } from '@shared/decision-concern';
import type { SuggestionDecision } from '@shared/factory-suggestions';

/**
 * T-14: the further changes the AI builder suggested on a task, under the description, newest run
 * first, with what was decided about each. T-72: only the runs whose suggestions are all decided, each
 * folded to one line that opens on a click; the ones still to decide are at the top of the task
 * (TaskToAnswer). The comments show a pointer line (FactorySuggestionsPointer) in place of each run's
 * block. Nothing shows without decided suggestions.
 */
export function TaskSuggestions({
  taskId,
  events,
  onNavigate,
  canStop = false,
}: {
  taskId: string;
  /** SF-T-304: the person can comment, so they can stop a decided suggestion's chat still running. */
  canStop?: boolean;
  /** The task's events, oldest first. */
  events: TaskEvent[];
  /** Called when a link to a new task is followed (closes a pop-up). */
  onNavigate?: () => void;
}) {
  const rounds = useMemo(() => suggestionRounds(events).filter(roundDecided), [events]);
  const taskRefs = useSuggestionTaskRefs(rounds);
  if (!rounds.length) return null;

  return (
    <section id={suggestionsAnchorId(taskId)} className="scroll-mt-4 space-y-2" aria-label="Suggestions">
      <h3 className="text-sm font-semibold">Suggestions</h3>
      <div className="space-y-2">
        {rounds.map((round) => (
          // SF-T-304: a decided round keeps its chats, read only, behind Show the chat (the answers stay on the task).
          <SuggestionRoundCard key={round.event.id} round={round} taskRefs={taskRefs} onNavigate={onNavigate} chat={{ taskId, canAsk: false, canStop }} />
        ))}
      </div>
    </section>
  );
}

/** One run's suggestions: folded once all are decided, open to decide while any aren't (with `onApply`). */
export function SuggestionRoundCard({
  round,
  taskRefs,
  onApply,
  onDismiss,
  onNavigate,
  upstream,
  chat,
  onPicksChange,
}: {
  round: SuggestionRound;
  /** The suggestions picked and not applied yet, on each change (FactorySuggestionsCard's). */
  onPicksChange?: (numbers: number[]) => void;
  /**
   * SF-T-304: each suggestion's live chat, on task `taskId` (`project`: the task's, when it isn't the tab's: the
   * Inbox). `canAsk`: the person can comment, so they can start one or carry it on (SuggestionChat.can_ask: not an admin's, unless they're one). Leave out in a preview.
   */
  chat?: { taskId: string; project?: string; canAsk: boolean; canStop?: boolean };
  /** The tasks the decisions made, loaded once for every round on the page (useSuggestionTaskRefs). */
  taskRefs?: ReadonlyMap<number, TaskRef>;
  /** Applies this run's picks (decideTaskSuggestions); resolves true when they were applied. Leave out in a preview. */
  onApply?: (eventId: string, decisions: Array<SuggestionPick & { number: number }>) => Promise<boolean>;
  /** SF-T-178: No thanks on this run's suggestions still open (declineTaskSuggestions). Leave out in a preview. */
  onDismiss?: (eventId: string) => Promise<boolean>;
  onNavigate?: () => void;
  /** T-58: the task's source, on a mirrored task: sent suggestions say so, and Send to <source> sends them. */
  upstream?: RoundUpstream;
}) {
  const askSlot = useItemChats(round.event.id, chat);
  return (
    <FactorySuggestionsCard
      id={suggestionRoundId(round.event.id)}
      askSlot={askSlot}
      fold={roundDecided(round)}
      foldDecided
      suggestions={round.suggestions}
      concerns={payloadConcerns(round.event.payload)}
      decided={decisionsOf(round)}
      taskRefs={taskRefs}
      onNavigate={onNavigate}
      onApply={onApply ? (decisions) => onApply(round.event.id, decisions) : undefined}
      onDismiss={onDismiss ? () => onDismiss(round.event.id) : undefined}
      onPicksChange={onPicksChange}
      upstream={
        upstream
          ? {
              sourceLabel: upstream.sourceLabel,
              sent: round.sent,
              proposals: round.proposals,
              waiting: upstream.waiting,
              onSend: upstream.onSend ? (numbers, mentionId) => upstream.onSend!(round.event.id, numbers, mentionId) : undefined,
              tagging: upstream.tagging,
            }
          : undefined
      }
      meta={
        <span data-hint={formatDateTime(new Date(round.event.created_at))}>
          {round.event.actor_label ? `${round.event.actor_label} · ` : null}
          {formatRelative(round.event.created_at)}
        </span>
      }
    />
  );
}

function decisionsOf(round: SuggestionRound) {
  return new Map([...round.decided].map(([n, d]) => [n, withOutcome(d.decision)]));
}

/** SF-T-308: a Backlog the AI builder reported on shows what it said: the task it made (linked), or why none. */
function withOutcome(decision: SuggestionDecision): SuggestionDecision {
  const outcome = decision.outcome;
  if (!outcome) return decision;
  return { ...decision, note: 'task' in outcome ? outcome.task : outcome.reason };
}
